import { apiClient } from './client';
import axios from 'axios';
import { Organization } from '@/types';

const B2B_BASE_URL = process.env.EXPO_PUBLIC_B2B_BASE_URL || 'http://localhost:8000';
const B2B_PARTNERS_URI = process.env.EXPO_PUBLIC_B2B_PARTNERS_URI || '/api/v2/partners';

// In-memory cache for organizations
let organizationsCache: Organization[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Clear the organizations cache (useful for testing or forced refresh)
 */
export const clearOrganizationsCache = (): void => {
  organizationsCache = null;
  cacheTimestamp = null;
};

/**
 * Fetch list of organizations for SSO login (with caching)
 */
export const fetchOrganizations = async (): Promise<Organization[]> => {
  // Check if cache is valid
  const now = Date.now();
  if (organizationsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION_MS) {
    console.log('Returning cached organizations list');
    return organizationsCache;
  }

  console.log('Fetching organizations from API...');

  // If B2B_BASE_URL is the same as API_BASE_URL (proxy mode), use apiClient for CORS/cookies
  const apiBaseUrl = process.env.EXPO_PUBLIC_API_BASE_URL || 'http://localhost:8000';
  const fullUrl = `${B2B_BASE_URL}${B2B_PARTNERS_URI}`;
  
  let organizations: Organization[];
  
  if (B2B_BASE_URL === apiBaseUrl) {
    // Use apiClient to maintain cookies and CSRF tokens
    const response = await apiClient.get<Organization[]>(B2B_PARTNERS_URI);
    organizations = response.data;
  } else {
    // Use standalone axios for different base URL
    const response = await axios.get<Organization[]>(fullUrl, {
      withCredentials: true,
    });
    organizations = response.data;
  }

  // Update cache
  organizationsCache = organizations;
  cacheTimestamp = now;

  return organizations;
};

/**
 * Generate SAML URL with RelayState parameter
 */
export const generateSamlUrl = (ssoUrl: string, relayState?: string): string => {
  if (!relayState) {
    return ssoUrl;
  }

  const url = new URL(ssoUrl);
  url.searchParams.set('RelayState', relayState);
  return url.toString();
};

/**
 * Determine special SSO routing based on next URL
 * Returns the appropriate SSO URL for coach dashboard or health guide platform
 */
export const getSpecialSSOUrl = (nextUrl?: string): string | null => {
  if (!nextUrl) return null;

  const coachDashboardUrl = process.env.EXPO_PUBLIC_SSO_COACH_DASHBOARD_URL;
  const healthGuideUrl = process.env.EXPO_PUBLIC_SSO_HEALTH_GUIDE_URL;

  if (nextUrl.startsWith('/coaching') || nextUrl.startsWith('/auditing')) {
    return coachDashboardUrl || null;
  }

  if (nextUrl.startsWith('/web/admin')) {
    return healthGuideUrl || null;
  }

  return null;
};
